// import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

// import FormList from "../../components/form/FormList";
import FormDependencyList from "../../components/form/FormDependencyList";
import InspectorConstraintList from "../../components/inspector/InspectorConstraintList";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorRow from "../../components/inspector/InspectorRow";
import Icon from "../../components/interface/Icon";
import { ConstraintResolutionOption } from "../../constants/constraints";
import { DependencyTypes } from "../../constants/dependencyTypes";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceUserActions from "../../constants/interfaceUserActions";
import PubSubTopics from "../../constants/pubSubTopics";
import { appStateOperations } from "../../modules/appState";
import batchUtils from "../../modules/batchUtils";
import { dateConstraintOperations } from "../../modules/dateConstraints";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { dependencyOperations } from "../../modules/dependencies";
import { itemOperations } from "../../modules/items";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import { trackEvent } from "../../utils/trackAnalytics";
import renderNotAllowedBox from "./renderNotAllowedBox";
import selectors from "./selectors";

const DependencyTypeOptions = [
  {
    label: "Finish to Start",
    value: DependencyTypes.FS
  },
  {
    label: "Finish to Finish",
    value: DependencyTypes.FF
  },
  {
    label: "Start to Start",
    value: DependencyTypes.SS
  },
  {
    label: "Start to Finish",
    value: DependencyTypes.SF
  }
];

const onResolveConstraintUsingOption = (optionId, itemIds, otherId, onResolveConstraintManually) => {
  if (optionId === ConstraintResolutionOption.MoveSelf) {
    onResolveConstraintManually(itemIds, otherId);
  } else {
    onResolveConstraintManually(otherId, itemIds);
  }
};

/*
const renderNarrativeConstraintsBox = useProps => {
  const values = [];
  const options = [];
  return useProps.typeData.canIncludeInNarrative ? (
    <InspectorGroup label="Narrative Constraints">
      <InspectorRow>
        <InspectorField>
          <FormList
            id="narrativeConstraints"
            values={values}
            options={options}
            canDelete={true}
            canAdd={true}
            allowSearch={true}
            onAddItem={_itemId => {}}
            onDeleteItem={_itemId => {}}
            title="Narrative Constraints"
            backText={getLabelWithUntitled(useProps.itemData.label)}
          />
        </InspectorField>
      </InspectorRow>
    </InspectorGroup>
  ) : (
    <InspectorGroup label="Narrative Constraints">
      <div className="groupUnavailable" onClick={useProps.onOpenSettingsClick}>
        <Icon icon="sidebarSettings" color="mid" />
        <span>{useProps.typeData.label} items cannot be added to narrative.</span>
      </div>
    </InspectorGroup>
  );
};
*/

const renderTimelineConstraintsBox = useProps => {
  return (
    <InspectorGroup label="Date Constraints">
      <InspectorRow>
        <InspectorConstraintList
          constraints={useProps.itemData.dateConstraints}
          calendar={useProps.calendar}
          constraintDependencyOptions={useProps.constraintDependencyOptions}
          onAddDateConstraint={(ids, subjectTarget, comparison, date) => {
            useProps.onAddDateConstraint(ids, useProps.itemIds.toArray(), subjectTarget, comparison, date);
          }}
          onAddRelativeConstraint={(ids, subjectTarget, object, objectTarget, comparison, offset) => {
            useProps.onAddRelativeConstraint(
              ids,
              useProps.itemIds.toArray(),
              subjectTarget,
              object,
              objectTarget,
              comparison,
              offset
            );
          }}
          onAddDatePartConstraint={(ids, subjectTarget, partPrecision, partValue) => {
            useProps.onAddDatePartConstraint(ids, useProps.itemIds.toArray(), subjectTarget, partPrecision, partValue);
          }}
          onItemClick={useProps.onItemClick}
          onDeleteClick={ids => useProps.onRemoveDateConstraints(ids, useProps.itemIds.toArray())}
          onResolveConstraintUsingOption={(optionId, otherId) => {
            onResolveConstraintUsingOption(optionId, useProps.itemIds, otherId, useProps.onResolveConstraintManually);
          }}
          title="Date Constraints"
          backText={getLabelWithUntitled(useProps.itemData.label)}
          onTogglePopover={useProps.onSetHasChildPopover}
          addInterfaceIdentifier={interfaceIdentifiers.INSPECTOR_DATE_CONSTRAINTS_PLUS}
          pulsateIconId={useProps.pulsateIconId}
        />
      </InspectorRow>
    </InspectorGroup>
  );
};

const renderDependenciesBox = useProps => {
  const blockingDeps = useProps.itemData.blockingDependencies;
  const blockingVals = blockingDeps
    .map(v => {
      return {
        label: getLabelWithUntitled(v.otherLabel),
        subValue: v.dependencyType,
        leftIcon: v.isViolated ? "statusConflict" : null,
        leftIconColor: v.isViolated ? "statusError" : null,
        value: v.other,
        isViolated: v.isViolated,
        resolutionOptions: v.resolutionOptions
      };
    })
    .toList()
    .toArray();

  const blockedByDeps = useProps.itemData.blockedByDependencies;
  const blockedByVals = blockedByDeps
    .map(v => {
      return {
        label: getLabelWithUntitled(v.otherLabel),
        subValue: v.dependencyType,
        leftIcon: v.isViolated ? "statusConflict" : null,
        leftIconColor: v.isViolated ? "statusError" : null,
        value: v.other,
        isViolated: v.isViolated,
        resolutionOptions: v.resolutionOptions
      };
    })
    .toList()
    .toArray();

  // TODO: In the future, there may be different options lists for
  // blocking and blockedBy dependencies.
  const options = useProps.dependencyOptions.toArray();

  return useProps.typeData.canHaveDate ? (
    <InspectorGroup label="Timeline Dependencies">
      <InspectorRow label="Blocked by">
        <InspectorField>
          <FormDependencyList
            id="blockedByDependencies"
            values={blockedByVals}
            options={options}
            subOptions={DependencyTypeOptions}
            canDelete={true}
            allowSearch={true}
            closeOnClick={true}
            onAddItem={subjectId =>
              useProps.onCreateDependency(
                subjectId,
                useProps.itemIds.toArray(),
                DependencyTypes.FS,
                useProps.itemIds.toArray()
              )
            }
            onSubValueChange={(subjectId, dependencyType) =>
              useProps.onChangeDependencyType(
                subjectId,
                useProps.itemIds.toArray(),
                dependencyType,
                useProps.itemIds.toArray()
              )
            }
            onDeleteItem={subjectId =>
              useProps.onRemoveDependencies(subjectId, useProps.itemIds.toArray(), useProps.itemIds.toArray())
            }
            onResolveConstraintUsingOption={(optionId, otherId) => {
              onResolveConstraintUsingOption(optionId, useProps.itemIds, otherId, useProps.onResolveConstraintManually);
            }}
            onItemClick={useProps.onItemClick}
            title="Blocked by"
            backText={getLabelWithUntitled(useProps.itemData.label)}
            onTogglePopover={useProps.onSetHasChildPopover}
            addInterfaceIdentifier={interfaceIdentifiers.INSPECTOR_BLOCKED_BY_PLUS}
            pulsateIconId={useProps.pulsateIconId}
          />
        </InspectorField>
      </InspectorRow>

      <InspectorRow label="Blocks">
        <InspectorField>
          <FormDependencyList
            id="blocksDependencies"
            values={blockingVals}
            options={options}
            subOptions={DependencyTypeOptions}
            canDelete={true}
            allowSearch={true}
            closeOnClick={true}
            onAddItem={itemId =>
              useProps.onCreateDependency(
                useProps.itemIds.toArray(),
                itemId,
                DependencyTypes.FS,
                useProps.itemIds.toArray()
              )
            }
            onSubValueChange={(itemId, dependencyType) =>
              useProps.onChangeDependencyType(
                useProps.itemIds.toArray(),
                itemId,
                dependencyType,
                useProps.itemIds.toArray()
              )
            }
            onDeleteItem={itemId =>
              useProps.onRemoveDependencies(useProps.itemIds.toArray(), itemId, useProps.itemIds.toArray())
            }
            onResolveConstraintUsingOption={(optionId, otherId) => {
              onResolveConstraintUsingOption(optionId, useProps.itemIds, otherId, useProps.onResolveConstraintManually);
            }}
            onItemClick={useProps.onItemClick}
            title="Blocks"
            backText={getLabelWithUntitled(useProps.itemData.label)}
            onTogglePopover={useProps.onSetHasChildPopover}
          />
        </InspectorField>
      </InspectorRow>
    </InspectorGroup>
  ) : (
    <InspectorGroup label="Date Constraints">
      <div className="groupUnavailable" onClick={useProps.onOpenSettingsClick}>
        <Icon icon="sidebarSettings" color="mid" />
        <span>{useProps.typeData.label} items cannot have dates.</span>
      </div>
    </InspectorGroup>
  );
};

const InspectorConstraintsTab = props => {
  return props.typeData.canHaveDate ? (
    <Fragment>
      {renderDependenciesBox(props)}
      {props.typeData.canHaveDate ? renderTimelineConstraintsBox(props) : null}
    </Fragment>
  ) : (
    renderNotAllowedBox("Constraints", "constraints", props.typeData.label, props.onOpenSettingsClick)
  );
};

InspectorConstraintsTab.propTypes = {
  includeNarrative: PropTypes.bool.isRequired,
  onCreateDependency: PropTypes.func.isRequired,
  onRemoveDependencies: PropTypes.func.isRequired,
  onChangeDependencyType: PropTypes.func.isRequired,

  onAddDateConstraint: PropTypes.func.isRequired,
  onAddRelativeConstraint: PropTypes.func.isRequired,
  onAddDatePartConstraint: PropTypes.func.isRequired,

  onRemoveDateConstraints: PropTypes.func.isRequired,
  onEditDateConstraints: PropTypes.func.isRequired,
  onOpenSettingsClick: PropTypes.func.isRequired,
  onSetHasChildPopover: PropTypes.func,
  itemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  itemData: PropTypeShapes.inspectorItem,
  typeData: PropTypeShapes.denormalisedTypeDefinition,
  dependencyOptions: PropTypeShapes.listOptionList,
  constraintDependencyOptions: PropTypeShapes.listOptionList,
  pulsateIconId: PropTypes.string
};

const mapStateToProps = (state, props) => {
  return {
    calendar: calendarSelectors.getCalendar(state.present),
    includeNarrative: definitionSelectors.isNarrativeAllowed(state.present),
    dependencyOptions: selectors.getDependencyOptions(state.present, props.itemIds),
    constraintDependencyOptions: selectors.getConstraintDependencyOptions(state.present, props.itemIds),
    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present)
  };
};

const readonlyActions = {
  onOpenSettingsClick: uiStateOperations.showSettingsModal,
  onItemClick: itemId => {
    PubSub.publish(PubSubTopics.scrollToItem, { id: itemId });
    return appStateOperations.selectItems([itemId]);
  }
};

const editingActions = {
  onCreateDependency: (subjectIds, itemIds, dependencyType, movableItems) => {
    trackEvent("createItemDependencyFromInspector", "setItemConstraint");
    return dependencyOperations.createDependencies(subjectIds, itemIds, dependencyType, movableItems);
  },
  onRemoveDependencies: (subjectIds, itemIds, movableItems) => {
    trackEvent("removeItemDependencyFromInspector", "setItemConstraint");
    return dependencyOperations.removeDependenciesWithValues(subjectIds, itemIds, movableItems);
  },
  onChangeDependencyType: (subjectIds, itemIds, dependencyType, movableItems) => {
    trackEvent("setItemDependencyFromInspector", "setItemConstraint");
    return dependencyOperations.changeDependencyTypeWithValues(subjectIds, itemIds, dependencyType, movableItems);
  },
  onAddDateConstraint: (removeIds, subjectIds, subjectTarget, comparison, date) => {
    trackEvent("setDateConstraintFromInspector", "setItemConstraint");
    let actions = [];
    if (removeIds) {
      actions.push(dateConstraintOperations.removeConstraints(removeIds, subjectIds));
    }
    actions.push(dateConstraintOperations.createDateConstraints(subjectIds, subjectTarget, comparison, date));
    actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.CREATE_DATE_CONSTRAINT));
    return batchUtils.createBatchAction(actions);
  },
  onAddRelativeConstraint: (removeIds, subjectIds, subjectTarget, object, objectTarget, comparison, offset) => {
    trackEvent("setDateConstraintFromInspector", "setItemConstraint");
    let actions = [];

    if (removeIds) {
      actions.push(dateConstraintOperations.removeConstraints(removeIds, subjectIds));
    }
    actions.push(
      dateConstraintOperations.createRelativeConstraints(
        subjectIds,
        subjectTarget,
        object,
        objectTarget,
        comparison,
        offset,
        subjectIds
      )
    );
    actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.CREATE_RELATIVE_CONSTRAINT));
    return batchUtils.createBatchAction(actions);
  },
  onAddDatePartConstraint: (removeIds, subjectIds, subjectTarget, partPrecision, partValue) => {
    trackEvent("setDateConstraintFromInspector", "setItemConstraint");
    let actions = [];
    if (removeIds) {
      actions.push(dateConstraintOperations.removeConstraints(removeIds, subjectIds));
    }
    actions.push(
      dateConstraintOperations.createDatePartConstraints(subjectIds, subjectTarget, partPrecision, partValue)
    );
    return batchUtils.createBatchAction(actions);
  },
  onRemoveDateConstraints: (ids, movableItems) => {
    trackEvent("setItemDateConstraint", "item");
    return dateConstraintOperations.removeConstraints(ids, movableItems);
  },
  onEditDateConstraints: (ids, movableItems) => {
    trackEvent("setItemDateConstraint", "item");
    return dateConstraintOperations.removeConstraints(ids, movableItems);
  },
  onResolveConstraintManually: itemOperations.resolveConstraintManually,
  onSetHasChildPopover: uiStateOperations.setLeftPanelHasChildPopover
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InspectorConstraintsTab);
